import { defineStore } from 'pinia'
import axios from '@/helpers/axios'
import { useMessageStore } from './message'
import recaptcha from '@/helpers/recaptcha'

const auth = axios.create({
  baseURL: import.meta.env.VITE_API_BASE
})
auth.defaults.withCredentials = true
auth.defaults.withXSRFToken = true

export const useAuthStore = defineStore('auth', {
  state: () => {
    return {
      user: {},
      next: null,
      session: sessionStorage.getItem('authenticated') ?? false
    }
  },
  getters: {
    getUser: (state) => state.user,
    companies: (state) => state.user?.companies ?? [],
    networks: (state) => state.user?.networks ?? [],
    memberships: (state) => state.user?.memberships ?? [],
    invitations: (state) => state.user?.invitations ?? [],
    isAdmin: (state) => state.user?.role === 'admin' ?? false,
    message: () => new useMessageStore(),
    authenticated: (state) => state.session
  },
  actions: {
    setNext(route) {
      this.next = route
    },
    setSession(value) {
      this.session = value
      sessionStorage.setItem('authenticated', value)
    },
    isSelf(id) {
      return this.user?.id === id || false
    },
    isCompanyAdmin(id) {
      return (
        this.companies
          .filter((company) => company.pivot.role === 'admin')
          .map((company) => company.id)
          .includes(parseInt(id)) || false
      )
    },
    isCompanyStandard(id) {
      return (
        this.isCompanyAdmin(id) ||
        this.companies
          .filter((company) => company.pivot.role === 'standard')
          .map((company) => company.id)
          .includes(parseInt(id)) ||
        false
      )
    },
    isGroupAdmin(id) {
      return (
        this.memberships
          .filter((membership) => membership?.role === 'admin')
          .map((membership) => membership?.group?.id)
          .includes(parseInt(id)) || false
      )
    },
    isGroupMember(id) {
      return (
        this.memberships.map((membership) => membership?.group?.id).includes(parseInt(id)) || false
      )
    },
    isNetworkAdmin(id) {
      return (
        this.networks
          .filter((network) => network.pivot.role === 'admin')
          .map((network) => network.id)
          .includes(parseInt(id)) || false
      )
    },
    isNetworkStandard(id) {
      return (
        this.isNetworkAdmin(id) ||
        this.networks
          .filter((network) => network.pivot.role === 'standard')
          .map((network) => network.id)
          .includes(parseInt(id)) ||
        false
      )
    },
    csrf() {
      return auth.get('sanctum/csrf-cookie')
    },
    async login(data) {
      const gtoken = await recaptcha.getToken()
      if (!gtoken) return false
      this.csrf().then(() => {
        auth
          .post('login', {
            gtoken,
            ...data
          })
          .then(() => {
            if (this.next) {
              this.$router.push(this.next)
              this.next = null
            } else {
              this.$router.push({ name: 'dashboard' })
            }
          })
          .catch((err) => {
            useMessageStore().setAlert({
              text: err.response?.data?.message,
              level: 'error'
            })
          })
      })
    },
    logout() {
      auth.post('logout').then(() => {
        this.$router.push({ name: 'login' }).then(() => {
          this.message.setAlert({
            text: 'Logged Out Successfully',
            level: 'success'
          })
          this.clear()
        })
      })
    },
    clear() {
      this.user = {}
      this.setSession(false)
      sessionStorage.clear()
    },
    async check(to) {
      return await axios
        .get('user')
        .then((response) => {
          this.user = response.data
          this.setSession(true)
          return true
        })
        .catch((err) => {
          if (this.authenticated === true) {
            this.message.setAlert({
              text: 'Session expired. Please log in again.',
              level: 'error'
            })
          }
          if (to.name !== 'login') {
            this.message.clear()
          }
          this.clear()
          return false
        })
    }
  }
})
